/**
 * PaymentRail.tsx — the Delivery & payment step's order rail: what the card is
 * held for (or charged) TODAY, then the plan beneath it, then the hold explained
 * and the trust cues (CTO 2026-09-24).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: journey look and feel,
 * workstream L4. Laid out by `CheckoutFlow`'s `.gps-pay-*` grid (tokens.css):
 * a sticky right-hand rail at ≥880px that mirrors the plan screen's, and on a
 * phone the next block after "Deliver to" — so the patient reads what they pay
 * BEFORE the card form, in either layout.
 *
 * TODAY LEADS (CTO: "Don't confuse them with what is going to get charged in the
 * next 3 months … That can have less prominence."). The one figure in display
 * type is the amount that meets the card at the moment of "Place order":
 *   - HOLD ROUTE — "Held on your card today … (your first month)". It is a
 *     pre-authorisation, not a payment: orders-service holds `order.monthlyPence`
 *     at attach and captures nothing until a clinician approves. So the headline
 *     never says "charged" or "Paid today". It used to carry a second line,
 *     "Not a payment — you're only charged if a clinician approves your
 *     treatment.", directly above the hold box whose heading says "This is a
 *     hold, not a payment" and whose first point says "You only pay if a
 *     clinician approves" — the same fact three times in one column (the CTO,
 *     2026-09-26: "we are saying the same thing over and over"). The line went;
 *     the hold box says it once, beside the figure.
 *   - CLINICIAN-LED ROUTE — "Paid today … (your first month)". orders captures
 *     the preapproved case's stored monthly at attach; the subscription bills
 *     the rest month by month.
 * The monthly price, the term total ("billed monthly") and delivery follow,
 * smaller and muted.
 *
 * ONE LOOK WITH THE PLAN SCREEN'S SUMMARY (2026-09-26, the CTO: "align to the
 * same in both screens"). The card, eyebrow, figure type, plan name and rows are
 * `OrderSummary.tsx`'s, the same pieces `PlanSelection`'s summary draws.
 *
 * ⚠️ EVERY FIGURE HERE IS `cardStepPrice`'S, NEVER ARITHMETIC. The headline and
 * the monthly row are the same `monthlyPence` (month 1 IS a month), and the term
 * total is the ladder rung's `termPricing(...).actualPence` or absent — see
 * `checkout/cardStepPrice.ts` for which server each lane's figure comes from.
 * No price ⇒ no figure and no money rows: never "£0", never "£NaN". On the HOLD
 * route the headline still shows, with "Your first month's price" in words where
 * the figure goes (`HELD_WITHOUT_FIGURE`) — the one place left that says what is
 * held today once the hold box stopped printing it.
 *
 * ⚠️ "Delivery — Free" IS `DELIVERY_SUMMARY_ROW`, A WORD, NEVER A FIGURE. Delivery
 * is priced and charged nowhere in the estate; see `checkout/deliveryCopy.ts`
 * for why a struck-through "was" price here would be a misleading reference
 * price.
 *
 * EDIT PLAN sits by the plan name. Whether it is OFFERED is decided entirely by
 * the caller (`CheckoutFlow`'s `canEditPlan`), because every reason to withhold
 * it is payment state this component cannot see; `onEditPlan` absent ⇒ the
 * control is absent, never disabled.
 */
import type { CSSProperties } from 'react';
import { formatPence } from '../lib/format';
import { Alert, Button, TrustStrip } from '../ui/kit';
import { CardHoldExplainer } from './CardHoldExplainer';
import type { CardStepPrice } from './cardStepPrice';
import { DELIVERY_SUMMARY_ROW } from './deliveryCopy';
import {
  orderSummaryCardStyle,
  SummaryEyebrow,
  SummaryFigure,
  SummaryPlanName,
  SummaryRow,
  summaryRowsStyle,
  termLabel as termLabelFor,
  termTotalLabel,
} from './OrderSummary';
import type { PaymentCaptureRoute } from './paymentCopy';

/** The rail's heading id — the summary is a region named by it. */
const PAY_RAIL_HEADING_ID = 'checkout-order-summary-heading';

/**
 * Said — never silently snapped back — when an Edit plan round trip could not
 * change the order's plan: orders answered 409 (the case's card setup is
 * already under way), or an orders build that predates the awaiting_payment
 * re-plan echoed the OLD plan back. Either way the rail shows the server's
 * plan, and this sentence says why it is not the one the patient just picked.
 * After a 409 it is shown only once CheckoutFlow has RE-READ the order and it
 * came back still awaiting payment — the plan "below" is then the one orders
 * holds, not the one this window remembered (journey UX review 2026-09-24).
 */
export const PLAN_CHANGE_REFUSED_MESSAGE =
  'We couldn’t change your plan because your card setup has already started, so your order below is unchanged. Please contact us if you’d like to change it.';

/**
 * A sentence the card step owes the patient after the server answered their own
 * action differently from what they asked (journey UX review 2026-09-24): a
 * refused or ignored Edit plan, or a pay attempt orders refused because the order
 * had moved on in another window. `at` says WHERE it belongs — beside the plan
 * (`plan`, in this rail) or above the safety gate it is about (`safety`, in the
 * pay column) — and there is only ever one on screen.
 */
export interface CheckoutNotice {
  message: string;
  at: 'plan' | 'safety';
}

/**
 * The one element a refusal notice is rendered in, wherever it sits — and the
 * target CheckoutFlow moves focus to once it has painted.
 *
 * WHY FOCUS, AND WHY NOT A LIVE REGION ALONE (review finding, 2026-09-24). The
 * notice arrives in the SAME commit as the screen it sits on: a refused Edit
 * plan swaps the plan screen for this step, and a refusal that re-reads the
 * order swaps a loader for it. A `role="status"` inserted with its text already
 * in it is not reliably announced (VoiceOver/Safari and NVDA/Firefox skip it),
 * and the stage change's view reset then moves focus to the top of the page — so
 * a screen-reader user was never told their change had been refused. Moving
 * focus HERE, after that reset has run, is announced by every screen reader and
 * also puts a keyboard user on the sentence instead of past it. `tabIndex={-1}`:
 * focusable by script, never a Tab stop.
 */
export const CHECKOUT_NOTICE_ID = 'checkout-notice';

export function CheckoutNoticeBlock({ message, style }: { message: string; style?: CSSProperties }) {
  return (
    <div id={CHECKOUT_NOTICE_ID} tabIndex={-1} style={style}>
      {/* `status`, never `alert`: it reports the outcome of the patient's own
          action, and the payment error keeps the surface's one assertive region. */}
      <Alert tone="warning" role="status">
        {message}
      </Alert>
    </div>
  );
}

export function PaymentRail({
  route,
  medicationName,
  months,
  price,
  trustPaymentCue,
  onEditPlan,
  planNotice,
}: {
  route: PaymentCaptureRoute;
  medicationName: string;
  months: number | null;
  /** `cardStepPrice`'s answer. Null ⇒ print no money at all. */
  price: CardStepPrice | null;
  /**
   * The route's payment chip in the trust row, or null for none. The HOLD route
   * passes null since 2026-09-26: its "No charge until approved" said again what
   * the hold box's approval point says right above it (`paymentCopy.ts`).
   */
  trustPaymentCue: string | null;
  /** Present ONLY while Edit plan is offered — see the header. */
  onEditPlan?: () => void;
  /**
   * A `plan` notice in words — a refused plan change
   * (`PLAN_CHANGE_REFUSED_MESSAGE`) or a plan changed in another window
   * (`CHECKOUT_PLAN_CHANGED_ELSEWHERE_MESSAGE`). See {@link CheckoutNotice}.
   */
  planNotice?: string | null;
}) {
  const termLabel = months === null ? null : termLabelFor(months);
  /* A headline above the plan line: the figure, or — hold route, no price — the words. */
  const hasHeadline = price !== null || route === 'hold';
  return (
    <div className="gps-pay-aside">
      {/* THE PIN (journey polish #7, 2026-09-24). The summary sticks INSIDE this
          box, not inside the whole rail. At ≥880px the rail is a flex column
          and this box takes whatever height the rail has left after the hold
          explainer and the trust cues (tokens.css, P7) — so the summary pins
          only as far as there is room and lets go before the page ends. Pinned
          inside the whole rail, it sat over the top of the hold explainer at
          the bottom of the page, with no scroll left to reveal it. */}
      <div className="gps-pay-rail-pin">
        <section
          className="gps-pay-rail-summary gps-jux-panel"
          aria-labelledby={PAY_RAIL_HEADING_ID}
          style={summaryCardStyle}
        >
          <SummaryEyebrow as="h2" id={PAY_RAIL_HEADING_ID}>
            Order summary
          </SummaryEyebrow>

          {price ? (
            /* TODAY, IN DISPLAY TYPE. One figure, and it is the one the card meets
               on "Place order" — never the term total. BOTH routes say it is the
               first month, in the same small suffix: the hold is month 1's price
               (orders' `createHold` on `order.monthlyPence`), and on the
               clinician-led route month 1 is what confirming takes. */
            <div className="gps-pay-today" style={{ margin: '0 0 var(--gps-s-3)' }}>
              <p style={todayLabelStyle}>{route === 'hold' ? 'Held on your card today' : 'Paid today'}</p>
              <div style={{ marginTop: '2px' }}>
                <SummaryFigure figure={formatPence(price.monthlyPence)} suffix="(your first month)" />
              </div>
            </div>
          ) : route === 'hold' ? (
            /* ⚠️ NO PRICE, AND STILL SAID ON THE HOLD ROUTE (review, 2026-09-26).
               `cardStepPrice` answers null for the 0-pence launch placeholder
               and for a term the catalogue does not price, and Place order is
               not gated on it (checkout.spec walks that route to "Order
               received"). The hold box no longer names what is held — its
               "You don't pay today. We just hold your first month's price" point
               went for repeating the figure above — so without this line
               nothing on the screen said that a hold is placed today, or what it
               is for. The same headline, with the words where the figure goes:
               never "£0", never a guessed amount. Clinician-led route: no line,
               as before — nothing there ever said it. */
            <div className="gps-pay-today" style={{ margin: '0 0 var(--gps-s-3)' }}>
              <p style={todayLabelStyle}>Held on your card today</p>
              <p style={noPriceHeldStyle}>{HELD_WITHOUT_FIGURE}</p>
            </div>
          ) : null}

          {/* THE PLAN, by name — and the control that changes it beside it. */}
          <div style={hasHeadline ? planLineStyle : { ...planLineStyle, paddingTop: 0, borderTop: 0 }}>
            <p style={{ margin: 0, minWidth: 0, flex: '1 1 10rem' }}>
              <SummaryPlanName name={medicationName} term={termLabel} />
            </p>
            {onEditPlan ? (
              <Button
                type="button"
                variant="ghost"
                onClick={onEditPlan}
                style={{ width: 'auto', flex: '0 0 auto', padding: '8px 16px' }}
              >
                Edit plan
              </Button>
            ) : null}
          </div>

          {planNotice ? (
            /* A refused change is SAID — and focused once painted, so it is heard
               (see `CHECKOUT_NOTICE_ID`). */
            <CheckoutNoticeBlock message={planNotice} style={{ marginTop: 'var(--gps-s-3)' }} />
          ) : null}

          {/* SMALLER AND MUTED — what the rest of the plan costs, and delivery. */}
          <div style={summaryRowsStyle}>
            {price ? <SummaryRow label="Monthly price" value={`${formatPence(price.monthlyPence)}/month`} /> : null}
            {/* "billed monthly" is part of the LABEL, so the term total cannot be
                read as one charge. A 1-month plan has no total row: it is the
                monthly, and it never renews. */}
            {price && price.termTotalPence !== null && months !== null && months > 1 ? (
              <SummaryRow label={termTotalLabel(months)} value={formatPence(price.termTotalPence)} />
            ) : null}
            <SummaryRow label={DELIVERY_SUMMARY_ROW.label} value={DELIVERY_SUMMARY_ROW.value} />
          </div>

          {price && price.changedFromPence !== null ? (
            /* A price that moved since the patient chose it is SAID, in text,
               not left for them to spot (and not colour-coded). */
            <p role="status" style={{ margin: '8px 0 0', fontSize: '14px', lineHeight: 1.5, color: 'var(--gps-text)' }}>
              The price of this plan has changed since you chose it &mdash; it is now {formatPence(price.monthlyPence)} a
              month.
            </p>
          ) : null}
        </section>
      </div>

      {/* The hold, explained beside the figure it holds (CTO 2026-09-11). It
          prints no figure of its own since 2026-09-26 — the headline above
          carries it, "(your first month)" included — so the two can never
          contradict. Hold route only: on the clinician-led route confirming
          takes the payment. */}
      {route === 'hold' ? <CardHoldExplainer /> : null}
      {/* Reassurance chips at the point of conversion (E3 #9) — the same chips
          the plan screen shows under its button. */}
      <TrustStrip paymentCue={trustPaymentCue} />
    </div>
  );
}

/* The summary card, sticky at ≥880px, with room under it before the hold box. */
const summaryCardStyle: CSSProperties = {
  ...orderSummaryCardStyle,
  marginBottom: 'var(--gps-s-3)',
};

/**
 * What the hold route's headline says in place of the figure when there is no
 * price to print (see the ⚠️ in the rail). The same fact the figure's
 * "(your first month)" suffix carries, in words.
 */
export const HELD_WITHOUT_FIGURE = 'Your first month’s price';

/* The no-figure line: body type, in the headline's place, so it reads as the
   answer to "Held on your card today" rather than as a caption. */
const noPriceHeldStyle: CSSProperties = {
  margin: '2px 0 0',
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: 1.35,
  color: 'var(--gps-text)',
};

const todayLabelStyle: CSSProperties = {
  margin: 0,
  fontSize: '14px',
  fontWeight: 700,
  color: 'var(--gps-text)',
};

const planLineStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 'var(--gps-s-2) var(--gps-s-3)',
  paddingTop: 'var(--gps-s-3)',
  borderTop: '1px solid var(--gps-border)',
};
